import { effect, Signal } from '@angular/core';
import { MessageService } from '../../services/message.service';

/**
 * Helper to automatically display error messages from store state
 * Creates an effect that watches the store's error signal and shows a toast when errors occur
 *
 * @param errorSignal - The error signal from the store
 * @param messageService - The MessageService instance to display errors
 * @param defaultTitle - Optional default title for error messages
 */
export function watchStoreErrors(
  errorSignal: Signal<string | null>,
  messageService: MessageService,
  defaultTitle = 'Error'
): void {
  effect(() => {
    const error = errorSignal();
    if (error) {
      messageService.error(error, defaultTitle);
    }
  });
}

/**
 * Helper to extract error message from HTTP error response
 * Provides consistent error message extraction across all stores
 *
 * @param err - The error object from HTTP call
 * @param defaultMessage - Fallback message if no error message found
 * @returns Extracted error message string
 */
export function extractErrorMessage(
  err: unknown,
  defaultMessage: string,
  options?: { notFoundMessage?: string }
): string {
  const error = err as { status?: number; error?: { message?: string }; message?: string } | null;
  // Handle specific HTTP status codes first to preserve legacy behavior
  if (error?.status === 404) {
    if (options?.notFoundMessage) {
      return options.notFoundMessage;
    }
    return `${defaultMessage} (404 - Not Found)`;
  }

  if (error?.status === 401) {
    return 'Authentication failed. Please ensure you are logged in.';
  }

  if (error?.status === 403) {
    return 'Access denied. You do not have permission to perform this action.';
  }

  if (error?.status === 0) {
    return 'Cannot connect to backend server. Please ensure the backend is running.';
  }

  // Then prefer backend-provided messages, falling back to generic defaults
  if (error?.error?.message) {
    return error.error.message;
  }

  // When HttpClient fails, `error.message` is often the generic "Http failure response...".
  // Prefer a stable default message unless the message is truly custom.
  const message = typeof error?.message === 'string' ? error.message : '';
  if (message && !message.startsWith('Http failure response')) {
    return message;
  }

  return defaultMessage;
}
